import React from 'react';
import { PropTypes } from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { Flex, H4, Modal, P } from '@decisiv/ui-components';
import Newspaper from '@decisiv/iconix/lib/components/Newspaper';

import useRetryExecutionFunctions from '../../hooks/useRetryExecutionFunctions';
import getEnvironment from '../../utils/getCurrentEnv';
import AdditionalInformation from './AdditionalInformation';
import ExecutionMetaRow from './ExecutionMetaRow';
import ExecutionModalAccordion from './ExecutionModalAccordion';
import ExecutionWizard from './ExecutionWizard';
import InvocationPayload from './InvocationPayload';
import PayloadSkeleton from './PayloadSkeleton';
import TraceIdField from './TraceIdField';
import useExecutionDetail from './useExecutionDetail';
import { formatRequestDetailUrl, traceIdOf } from './executionLinks';
import { NoWrapRow } from './style';

const REFRESH_DELAY_MS = 3000;

export default function LogDetailsModal({
  log = {},
  visibility,
  handleVisibility,
  modalTitle = 'Execution Details',
  logs,
  onRefreshLogs = () => {},
  loading = false,
  invocationPayload = '',
  executionStatus = null,
  integrationName = '',
}) {
  const retry = useRetryExecutionFunctions();
  const { detailLog, detailError, parentLog } = useExecutionDetail({
    visibility,
    log,
  });

  const requestDetailUrl = formatRequestDetailUrl(log, parentLog);
  const isRequestDetailReady = Boolean(traceIdOf(log));

  // Until the record carries a status there is nothing to lay out around, so
  // the modal shows only the payload it was opened with.
  const showFullChrome = !loading && Boolean(log?.status);

  const renderPayloadSection = () => {
    if (detailLog) return <ExecutionModalAccordion content={detailLog} />;

    if (detailError) {
      return (
        <P color="alaskanHusky">
          <Trans>Unable to load execution payload.</Trans>
        </P>
      );
    }

    return <PayloadSkeleton />;
  };

  const closeAndRefresh = () =>
    setTimeout(() => {
      handleVisibility(false);
      onRefreshLogs();
    }, REFRESH_DELAY_MS);

  return (
    <Modal
      actions={[
        {
          text: t`Close`,
          onClick: () => handleVisibility(false),
          kind: 'secondary',
        },
      ]}
      intent="information"
      icon={Newspaper}
      title={modalTitle || t`Execution Details`}
      visible={visibility}
      onClose={() => handleVisibility(false)}
    >
      <Flex flexDirection="column">
        {executionStatus && (
          <ExecutionWizard
            executionStatus={executionStatus}
            integrationName={integrationName}
          />
        )}

        {!showFullChrome && (
          <Flex flexDirection="column">
            {invocationPayload && (
              <InvocationPayload payload={invocationPayload} />
            )}
          </Flex>
        )}

        {showFullChrome && (
          <Flex flexDirection="column">
            <Flex marginTop={1}>
              <H4 weight="extrabold">{log?.integration_name}</H4>
            </Flex>
            <Flex>
              <P weight="semibold">{log?.location_name}</P>
            </Flex>
            <Flex>
              <P color="charcoal" weight="semibold">
                <Trans>Environment:</Trans>&nbsp;
              </P>
              <P weight="regular" color="alaskanHusky">
                {getEnvironment(process.env.REACT_APP_BASE_URL)}
              </P>
            </Flex>

            <NoWrapRow>
              <Flex flexDirection="column">
                <TraceIdField
                  confirmCopy
                  label={<Trans>X-Decisiv-Trace-ID:</Trans>}
                  traceId={log?.x_decisiv_trace_id}
                />
                {log?.parent_x_decisiv_trace_id &&
                  !log?.response_from_cache && (
                    <TraceIdField
                      label={<Trans>Parent-X-Decisiv-Trace-ID:</Trans>}
                      traceId={log.parent_x_decisiv_trace_id}
                    />
                  )}
              </Flex>
              <Flex flexDirection="column" marginLeft={2}>
                {log?.response_from_cache && (
                  <TraceIdField
                    label={<Trans>Cache Source X-Decisiv-Trace-ID:</Trans>}
                    traceId={log?.parent_x_decisiv_trace_id}
                  />
                )}
              </Flex>
            </NoWrapRow>

            <ExecutionMetaRow
              log={log}
              logs={logs}
              retry={retry}
              onRetried={closeAndRefresh}
            />

            <AdditionalInformation
              log={log}
              requestDetailUrl={requestDetailUrl}
              isRequestDetailReady={isRequestDetailReady}
            />

            <Flex marginTop={1} flexDirection="column">
              <Flex marginTop={1}>{renderPayloadSection()}</Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
    </Modal>
  );
}

LogDetailsModal.propTypes = {
  log: PropTypes.oneOfType([PropTypes.object]),
  logs: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  visibility: PropTypes.bool.isRequired,
  handleVisibility: PropTypes.func.isRequired,
  modalTitle: PropTypes.string,
  onRefreshLogs: PropTypes.func,
  loading: PropTypes.bool,
  invocationPayload: PropTypes.string,
  executionStatus: PropTypes.shape({
    status: PropTypes.string,
    startDate: PropTypes.string,
  }),
  integrationName: PropTypes.string,
};
